import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { MawLogoFull } from '../shared/MawLogo';
import { v4 as uuidv4 } from 'uuid';

const STEPS = [
  { id: 'welcome',     title: 'Bem-vindo ao MAW Finanças!',    icon: 'fa-star' },
  { id: 'account',     title: 'Crie sua primeira conta',        icon: 'fa-building-columns' },
  { id: 'transaction', title: 'Registre sua renda mensal',      icon: 'fa-coins' },
  { id: 'done',        title: 'Tudo pronto!',                   icon: 'fa-circle-check' },
];

const accountGradients = {
  '#0d1b2a': 'linear-gradient(135deg,#0d1b2a,#1e3a52)',
  '#1D9E75': 'linear-gradient(135deg,#0F6E56,#1D9E75)',
  '#185FA5': 'linear-gradient(135deg,#0c447c,#378ADD)',
  '#8A05BE': 'linear-gradient(135deg,#5c0381,#8A05BE)',
  'var(--gold)': 'linear-gradient(135deg,#8B6914,var(--gold))',
  '#A32D2D': 'linear-gradient(135deg,#791F1F,#e24b4a)',
};

export default function Onboarding({ onDone }) {
  const { dispatch } = useApp();
  const [step, setStep] = useState(0);
  const [account, setAccount] = useState({ name: '', type: 'corrente', balance: '', color: '#0d1b2a' });
  const [accId]  = useState(uuidv4());
  const [income, setIncome] = useState({ description: 'Salário', value: '', categoryId: 'sal' });

  const next = () => setStep(s => s + 1);

  const saveAccount = () => {
    if (!account.name.trim()) return;
    dispatch({
      type: 'ADD_ACCOUNT',
      payload: { id: accId, ...account, balance: parseFloat(account.balance) || 0, active: true },
    });
    next();
  };

  const saveIncome = () => {
    const val = parseFloat(income.value);
    if (!isNaN(val) && val > 0) {
      dispatch({
        type: 'ADD_TRANSACTION',
        payload: {
          description: income.description || 'Salário',
          value: val,
          type: 'income',
          accountId: accId,
          categoryId: income.categoryId,
          date: new Date().toISOString().slice(0, 10),
          status: 'paid',
          recurrent: false,
          notes: 'Criado durante o onboarding',
        },
      });
    }
    next();
  };

  const skip = () => {
    localStorage.setItem('maw_onboarding_done', '1');
    onDone();
  };

  const finish = () => {
    localStorage.setItem('maw_onboarding_done', '1');
    onDone();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 8000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: 'var(--card-bg)', borderRadius: 20, width: '100%', maxWidth: 480,
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        overflow: 'hidden',
      }}>
        {/* Progress bar */}
        <div style={{ height: 4, background: 'var(--border)' }}>
          <div style={{
            height: '100%', background: 'var(--gold)',
            width: `${((step + 1) / STEPS.length) * 100}%`,
            transition: 'width 0.4s ease',
          }} />
        </div>

        <div style={{ padding: '32px 32px 28px' }}>
          {/* Step dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 28 }}>
            {STEPS.map((s, i) => (
              <div key={s.id} style={{
                width: i === step ? 24 : 8, height: 8, borderRadius: 99,
                background: i <= step ? 'var(--gold)' : 'var(--border)',
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>

          {/* ── Step 0: Bem-vindo ── */}
          {step === 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 20 }}><MawLogoFull size={1} /></div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 12 }}>
                Bem-vindo ao MAW Finanças!
              </h2>
              <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 28 }}>
                Seu sistema completo de controle financeiro. Em menos de 2 minutos você estará pronto para organizar suas finanças.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 28 }}>
                {[
                  { icon: 'fa-building-columns', label: 'Contas e Cartões' },
                  { icon: 'fa-chart-line',        label: 'Relatórios' },
                  { icon: 'fa-star',              label: 'Metas' },
                ].map(f => (
                  <div key={f.label} style={{ background: 'var(--surface2)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
                    <i className={`fas ${f.icon}`} style={{ fontSize: 20, color: 'var(--gold)', marginBottom: 8, display: 'block' }}></i>
                    <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 600 }}>{f.label}</div>
                  </div>
                ))}
              </div>
              <button onClick={next} className="btn btn-primary btn-full" style={{ padding: 14, fontSize: 15 }}>
                <i className="fas fa-arrow-right"></i> Começar configuração
              </button>
              <button onClick={skip} style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', width: '100%' }}>
                Pular e configurar depois
              </button>
            </div>
          )}

          {/* ── Step 1: Criar conta ── */}
          {step === 1 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg,var(--navy),var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <i className="fas fa-building-columns" style={{ fontSize: 24, color: '#fff' }}></i>
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>Crie sua primeira conta</h2>
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>Adicione a conta bancária que você usa no dia a dia</p>
              </div>

              <div className="form-group">
                <label className="form-label">Nome da conta *</label>
                <input className="form-control" placeholder="Ex: Itaú Conta Corrente, Nubank..." value={account.name} onChange={e => setAccount(a => ({ ...a, name: e.target.value }))} autoFocus />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-control" value={account.type} onChange={e => setAccount(a => ({ ...a, type: e.target.value }))}>
                    <option value="corrente">Conta Corrente</option>
                    <option value="poupanca">Poupança</option>
                    <option value="investimento">Investimento</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Saldo atual (R$)</label>
                  <input className="form-control" type="number" placeholder="0,00" value={account.balance} onChange={e => setAccount(a => ({ ...a, balance: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Cor</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.keys(accountGradients).map(c => (
                    <div key={c} onClick={() => setAccount(a => ({ ...a, color: c }))} style={{ width: 30, height: 30, borderRadius: '50%', background: c, cursor: 'pointer', border: account.color === c ? '3px solid var(--gold)' : '3px solid transparent', transition: 'all 0.15s' }} />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={skip} className="btn btn-ghost" style={{ flex: 1 }}>Pular</button>
                <button onClick={saveAccount} className="btn btn-primary" style={{ flex: 2, padding: 12 }} disabled={!account.name.trim()}>
                  <i className="fas fa-check"></i> Criar conta e continuar
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Registrar renda ── */}
          {step === 2 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg,#0F6E56,#1D9E75)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <i className="fas fa-coins" style={{ fontSize: 24, color: '#fff' }}></i>
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>Qual é a sua renda mensal?</h2>
                <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
                  Registre seu salário ou receita principal. O MAW usa isso para calcular sua saúde financeira.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Descrição</label>
                <input
                  className="form-control"
                  placeholder="Ex: Salário, Freelance..."
                  value={income.description}
                  onChange={e => setIncome(v => ({ ...v, description: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Valor (R$) *</label>
                <input
                  className="form-control"
                  type="number"
                  placeholder="0,00"
                  step="0.01"
                  min="0"
                  value={income.value}
                  onChange={e => setIncome(v => ({ ...v, value: e.target.value }))}
                />
              </div>

              {/* Preview card */}
              {parseFloat(income.value) > 0 && (
                <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="fas fa-arrow-up" style={{ color: '#1d9e75' }}></i>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#065f46' }}>{income.description || 'Receita'}</div>
                    <div style={{ fontSize: 12, color: '#047857' }}>
                      R$ {parseFloat(income.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} — será registrado hoje como receita paga
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={next} className="btn btn-ghost" style={{ flex: 1 }}>Pular</button>
                <button
                  onClick={saveIncome}
                  className="btn btn-primary"
                  style={{ flex: 2, padding: 12 }}
                  disabled={!income.value || parseFloat(income.value) <= 0}
                >
                  <i className="fas fa-check"></i> Registrar e continuar
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Concluído ── */}
          {step === 3 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 12 }}>
                Tudo pronto!
              </h2>
              <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 24 }}>
                Sua conta <strong style={{ color: 'var(--text)' }}>{account.name || 'principal'}</strong> foi criada e sua renda registrada. Agora você já pode controlar tudo pelo Dashboard!
              </p>

              <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 16, marginBottom: 28, textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Próximos passos</div>
                {[
                  { icon: 'fa-plus',        color: '#1d9e75', text: 'Adicione seus gastos com "Novo Lançamento"' },
                  { icon: 'fa-credit-card', color: '#378ADD', text: 'Cadastre seus cartões de crédito' },
                  { icon: 'fa-bullseye',    color: 'var(--gold)', text: 'Crie metas de economia para alcançar seus objetivos' },
                  { icon: 'fa-robot',       color: '#8A05BE', text: 'Use o Consultor IA para análises personalizadas' },
                ].map((tip, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: i < 3 ? 10 : 0 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${tip.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`fas ${tip.icon}`} style={{ fontSize: 11, color: tip.color }}></i>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, paddingTop: 5 }}>{tip.text}</span>
                  </div>
                ))}
              </div>

              <button onClick={finish} className="btn btn-primary btn-full" style={{ padding: 14, fontSize: 15 }}>
                <i className="fas fa-rocket"></i> Ir para o Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
